import { ModalRef, useApiClient, useMicroservices, useTranslation } from '@msdk/common'
import {
  CountBehaviour,
  EntityMetadata,
  EntityModel,
  GenericFieldValue,
  LinkDefinition,
  Microservice,
  readBaseMetadataByName,
  readBaseMetadataByNameAndModule,
  readEntityModelIncludeExternal,
  ViewModel,
} from '@msdk/definitions'
import { ButtonIcon, CloseIcon, FormHeaderWrapper, styled, Tabs, TabsContainer } from '@msdk/ui'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'

import { $countCollection, CountCollectionEntry } from '../../../state'
import { parseSize } from '../../../utils'
import { I18nWrapper } from '../../I18nWrapper'
import { ModalFederationWrapper } from '../../ModalFederationWrapper'
import { ModalSuspenseWrapper } from '../../ModalSuspenseWrapper/ModalSuspenseWrapper'
import { SignleModalView } from '../../ModalView/SingleModalView'
import { useViewContext, ViewContextProvider } from '../../ViewContextProvider'
import { ViewHeader } from '../../ViewHeader'

const Wrapper = styled('div', { shouldForwardProp: (prop) => prop !== '$width' && prop !== '$height' })<{
  $width?: string
  $height?: string
}>`
  height: ${(props) => props.$height || '100%'};
  width: ${(props) => props.$width || '100%'};
`

interface Props {
  links: LinkDefinition[]
  filters?: Record<string, GenericFieldValue>
  modalRef?: ModalRef
  modalSize?: string
}

interface CurrentLink {
  linkedEntity: string
  viewName: string
}

function LookupModalContent(props: Props) {
  const t = useTranslation()
  const api = useApiClient()

  const [countCollection, setCountCollection] = useRecoilState($countCollection)
  const [currentTab, setCurrentTab] = useState<number>(0)
  const microservices: Microservice[] = useMicroservices()
  const validLinks: LinkDefinition[] = []
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const viewContext = useViewContext()

  const onTabChange = (newValue: number) => {
    setIsInitialized(false)
    setCurrentTab(newValue)
  }

  useEffect(() => {
    if (viewContext.context.isInitialized) {
      setIsInitialized(true)
    }
  }, [viewContext])

  useEffect(() => {
    const fetch = async () => {
      const entries: CountCollectionEntry[] = []
      for (let i = 0; i < validLinks.length; i++) {
        const currentPair = validLinks[i]
        const baseMetadata = readBaseMetadataByNameAndModule(
          currentPair.linkedEntity,
          currentPair.viewName,
        ) as EntityMetadata
        const model = readEntityModelIncludeExternal(currentPair.linkedEntity, baseMetadata.msProvider) as EntityModel
        const viewModel = model.getView(currentPair.viewName) as ViewModel
        if (viewModel.countBehaviour === CountBehaviour.Enabled) {
          const metadata = readBaseMetadataByNameAndModule(
            currentPair.linkedEntity,
            currentPair.viewName,
          ) as EntityMetadata
          const countRes = await api.entity.getViewCount(
            metadata.msProvider,
            currentPair.linkedEntity,
            currentPair.viewName,
          )
          entries.push({
            entityName: currentPair.linkedEntity,
            viewName: currentPair.viewName,
            count: countRes.count,
          })
        }
      }
      setCountCollection((curr) => {
        const newCollection = curr.clone()
        entries.forEach((entry) => {
          newCollection.upsertItem(entry.entityName, entry.viewName, entry.count)
        })
        return newCollection
      })
    }
    fetch().catch(console.error)
  }, [])

  const getViewLabel = (entityName: string, viewName: string) => {
    const baseMetadata = readBaseMetadataByNameAndModule(entityName, viewName) as EntityMetadata
    const model = readEntityModelIncludeExternal(entityName, baseMetadata.msProvider) as EntityModel
    const viewModel = model.getView(viewName) as ViewModel
    const count = countCollection.getItem(entityName, viewName)
    if (viewModel.countBehaviour === CountBehaviour.Enabled) {
      return `${t(viewModel.idml, viewModel.description)}${count !== undefined ? `: ${count}` : ''}`
    }
    return `${t(viewModel.idml, viewModel.description)}`
  }

  for (const link of props.links) {
    const baseMetadata = readBaseMetadataByName(link.linkedEntity) as EntityMetadata
    const microservice = microservices.find((m) => m.name === baseMetadata?.msProvider)

    if (microservice && microservice.isOk) {
      validLinks.push(link)
    }
  }

  let currentLink: CurrentLink | undefined = undefined

  if (validLinks.length > 0) {
    currentLink = validLinks[currentTab]
  }

  const size = parseSize(props.modalSize)

  return (
    <Wrapper $height={size?.height} $width={size?.width}>
      <FormHeaderWrapper $centered={false} $mode="Modal" className="modal-form-header" $isModal={true}>
        {currentLink && (
          <ViewHeader
            entityName={currentLink.linkedEntity}
            viewName={currentLink.viewName}
            mode="Modal"
            isInitialized={isInitialized}
          />
        )}
        <ButtonIcon onClick={() => props.modalRef?.close()}>
          <CloseIcon />
        </ButtonIcon>
      </FormHeaderWrapper>
      <TabsContainer className="modal-tabs-content">
        <Tabs
          onChange={onTabChange}
          childs={validLinks.map((link) => {
            return {
              label: getViewLabel(link.linkedEntity, link.viewName),
              content: (
                <ModalSuspenseWrapper modalRef={props.modalRef}>
                  <ModalFederationWrapper
                    entityName={link.linkedEntity}
                    moduleName={link.viewName}
                    modalRef={props.modalRef}
                  >
                    <I18nWrapper entityName={link.linkedEntity} moduleName={link.viewName} modalRef={props.modalRef}>
                      <SignleModalView
                        entityName={link.linkedEntity}
                        viewName={link.viewName}
                        modalRef={props.modalRef}
                        prefilledFilters={props.filters}
                        lookup
                      />
                    </I18nWrapper>
                  </ModalFederationWrapper>
                </ModalSuspenseWrapper>
              ),
            }
          })}
        ></Tabs>
      </TabsContainer>
    </Wrapper>
  )
}

export function LookupModal(props: Props) {
  return (
    <ViewContextProvider>
      <LookupModalContent {...props} />
    </ViewContextProvider>
  )
}
